import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Route, Router } from '@angular/router';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { CompleteSyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { SyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-generate-syllabus';
import { ManualCourse } from '@edtech-ui/core/models/manual-course/manual-couse-payload';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { UnpublishedCourseDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/create-course/unpublished-course-dialog-box/unpublished-course-dialog-box.component';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { ToastrService } from 'ngx-toastr';
import { of, Subscription, tap } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-manual-course-preview',
  standalone: true,
  imports: [CommonModule, FormsModule, NgIf, NgForOf,MatIcon],
  templateUrl: './manual-course-preview.component.html',
  styleUrls: ['./manual-course-preview.component.scss']
})
export class ManualCoursePreviewComponent implements OnInit {
  classroomId: any = '';
  courseId: string = '';
  coursePayload: ManualCourse = {
    course_code: '',
    course_title: '',
    grade_level: '',
    credits: 0,
    course_description: '',
    course_duration: 0,
    internal_marks: 0,
    exam_marks: 0,
    total_units_count: 0,
    course_objectives: [],
    course_outcomes: [],
    units: [],
    textbooks: [],
    web_references_links: [],
    status: 'Draft'
  };
  isLoadingDraft = false;
  isLoadingPublish = false;
  private subscription: Subscription = new Subscription();
  isPublish: boolean = false;
  _syllabusData: SyllabusResponseAI['Data'] | null = null;
  status: CourseStatusEnum | null = null;
  classroomid!: number;
  courseStatus = CourseStatusEnum;


  constructor(
    private courseService: CourseManagementService,
    private _toast: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private _toastr: ToastrService,
    private dialog: MatDialog,

  ) { }

  ngOnInit(): void {
    this.route.parent?.paramMap.subscribe(params => {
      this.classroomId = params.get('classroomId') ?? '';
    });
    this.route.queryParamMap.subscribe(params => {
      const courseId = params.get('courseId');
      if (courseId) {
        this.courseId = courseId;
        this.loadCourseData();
      }
    });
    this.subscription.add(
      this.courseService.payloadChanges().subscribe(payload => {
        if (!this.courseId && payload) {
          this.coursePayload = payload;
        }
      })
    );
     this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
    if (!e.url.includes('/manual-course-creation')) {
      this.courseService.clearPayload(); 
    }
  });
  }

  loadCourseData() {
    if (this.courseId) {
      this.subscription.add(
        this.courseService.getCoursesByCourseId(this.courseId)
          .pipe(
            tap((response: CompleteSyllabusResponseAI) => {
              if (response?.Success && response.Data) {
                this._syllabusData = response.Data;
                this.coursePayload = response.Data as any;
                this.status = response.Data.status as CourseStatusEnum;
              }
            }),
            catchError(err => {
              this._toastr.error('Error fetching course');
              return of(null);
            })
          )
          .subscribe()
      );
    } else {
      const payload = this.courseService.getPayload();
      if (payload) {
        this.coursePayload = payload;
      }
    }
  }

  saveAsDraft(): void {
    this.postManualCourse(this.coursePayload, CourseStatusEnum.Draft);
    this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);

  }

  publishCourse(): void {
    this.postManualCourse(this.coursePayload, CourseStatusEnum.Published);
    this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
  }

  postManualCourse(payload: ManualCourse, status: CourseStatusEnum): void {
    if (!this.classroomId) {
      this._toast.error('Classroom ID is required');
      return;
    }

    status === CourseStatusEnum.Draft
      ? this.isLoadingDraft = true
      : this.isLoadingPublish = true;

    const preparedPayload: ManualCourse = {
      ...payload,
      credits: Number(payload.credits || 0),
      internal_marks: Number(payload.internal_marks || 0),
      exam_marks: Number(payload.exam_marks || 0),
      course_duration: Number(payload.course_duration || 0),
      total_units_count: payload.units?.length || 0,
      status: status === CourseStatusEnum.Draft ? 'Draft' : 'Published',
      units: (payload.units || []).map((unit, unitIndex) => ({
        unit_id: unit.unit_id ?? undefined,
        unit_title: String(unit.unit_title || ''),
        unit_hours: Number(unit.unit_hours || 0),
        order: unitIndex + 1,
        topics: (unit.topics || []).map((topic, topicIndex) => ({
          topic_id: topic.topic_id ?? undefined,
          topic_title: String(topic.topic_title || ''),
          description: topic.description || '',
          order: topicIndex + 1,
          subtopics: (topic.subtopics || []).map(sub => ({
            subtopic_title: sub.subtopic_title || '',
            description: sub.description || '',
            reference_links: Array.isArray(sub.reference_links)
              ? sub.reference_links
              : []
          }))
        }))
      })),

      web_references_links: (payload.web_references_links || [])
        .map(link => typeof link === 'string' ? link : '')
    };

    let request$;
    if (this.courseId) {
      // Existing course update in correct API
      request$ = this.courseService.patchPublishedCourseByStatus(
        this.classroomId,
        this.courseId
      );
    } else {
      // Create new course
      request$ = this.courseService.saveCourse(preparedPayload);
    }

    // Execute request
    request$
      .pipe(
        tap((response: any) => {
          this.isLoadingDraft = false;
          this.isLoadingPublish = false;

          if (response?.Success) {
            this._toast.success(
              status === CourseStatusEnum.Draft
                ? 'Course saved as draft successfully!'
                : 'Course published successfully!'
            );

            this.courseService.clearPayload();

            if (status === CourseStatusEnum.Published) {
              this.router.navigate([
                '/teacher-dashboard',
                this.classroomId,
                'manual-course-creation',
                'course-preview'
              ]);
            }
          } else {
            this._toast.error(response?.Message || 'Failed to save course');
          }
        }),
        catchError(() => {
          this.isLoadingDraft = false;
          this.isLoadingPublish = false;
          this._toast.error('Something went wrong while saving the course');
          return of(null);
        })
      )
      .subscribe();
  }

  getCourseDetailsById() {
    if (this.courseId && this.classroomId) {
      this.subscription.add(
        this.courseService.getCoursesByCourseId(this.courseId).subscribe({
          next: (response: CompleteSyllabusResponseAI) => {
            if (response.Success && response.Data) {
              this._syllabusData = response.Data;
              this.status = response.Data.status as CourseStatusEnum;
            }
          }
        })
      );
    }
  }
  openDeleteCourseDialogBox(courseId: string) {
    this.dialog.open(DeleteDialogBoxComponent, {
      width: '360px',
      data: { courseId }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.deleteCoursesById(courseId)
      }
    });
  }

  deleteCoursesById(courseId: string) {
    this.subscription.add(
      this.courseService.deleteCourseById(courseId).subscribe({
        next: () => {
          this._toastr.success('Course deleted successfully');
          this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
        },
        error: () => {
          this._toastr.error('Failed to delete course');
        }
      })
    );
  }

  openUnpublishDialogBox() {
    this.dialog.open(UnpublishedCourseDialogBoxComponent, {
      width: '400px',
      data: { courseId: this.courseId }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.patchPublishedCourseByStatus()
      }
    });
  }

  patchPublishedCourseByStatus() {
    if (!this.classroomId) {
      this._toastr.error('Classroom ID is required');
      return;
    }
    this.isPublish = true;
    this.subscription.add(
      this.courseService.patchPublishedCourseByStatus(this.classroomId, this.courseId).pipe(
        tap(response => {
          if (response.Success) {
            this.getCourseDetailsById();
            const courseStatus = response.Data?.status;
            if (courseStatus === CourseStatusEnum.Published) {
              this._toastr.success('Your course has been published and is now available to students');
              this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
            } else if (courseStatus === CourseStatusEnum.Draft) {
              this._toastr.success('Your course was Published and is now moved to Draft.');
            }
          } else {
            this._toastr.error(response.Message);
          }
        }),
        finalize(() => {
          this.isPublish = false;
        }),
        catchError(() => {
          this.isPublish = false;
          return of(null);
        })
      ).subscribe()
    );
  }
  goToPrePage() {
    this.router.navigate(['/teacher-dashboard', this.classroomId, 'manual-course-creation', 'add-resources']);
  }
  goBackToPreviousStep() {
    this.router.navigate([
      '/teacher-dashboard',
      this.classroomId,
      'manual-course-creation',
      'add-resources'
    ]);
  }
  goBack(): void {
    if (this.classroomId) {
      this.router.navigate([
        '/teacher-dashboard',
        'classroom-detail-page',
        this.classroomId,
        'courses'
      ]);
    } else {
      this._toastr.error('Classroom ID not found!');
    }
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
